/** @prettier */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {SelectChangeEvent} from '@mui/material/Select';
import {useParams} from 'react-router-dom';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {postSettingSites} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {format} from '../../define';

import {useTranslation} from 'react-i18next';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';

/**
 * ユーザ追加ダイアログ
 * @param props props
 * @returns JSX
 */
const UserAddDialog = (props: {
  setAuthInfoChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  const [mailAddress, setMailAddress] = useState(''); //メールアドレス
  //prettier-ignore
  const [authorization, setAuthorization] = useState<apiIF.reqParameters.postSettingSites['site-authority']>('viewer'); /* 権限 */
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false); //追加ダイアログ表示フラグ
  const [mailAddrErrMessage, setMailAddrErrMessage] = useState(''); //メールアドレスエラーメッセージ
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [mailAddressError, setMailAddressError] = useState(false);

  //入力メールアドレスのエラーチェック
  useEffect(() => {
    if (mailAddressError || mailAddress === '' || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [mailAddressError, mailAddress, authorization, apiWait]);

  /**
   * ADD USERボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenAddDialog(true);
  };

  /**
   * OKボタン押下時の処理
   * @returns
   */
  const handleOK = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //OKボタンを非表示、API待ちにする
    setOKBtnDisabled(true);
    setAPIWait(true);

    //入力メールアドレスのフォームチェック
    const regex = format.EMAIL_PATTERN_OK;
    if (regex.test(mailAddress) === false) {
      setMailAddrErrMessage('FormatFailure');
      setOKBtnDisabled(false);
      setAPIWait(false);
      return;
    }

    const addUserInfo: apiIF.reqParameters.postSettingSites = {
      'e-mail': mailAddress,
      'site-authority': authorization,
    };
    //ユーザ登録
    postSettingSites(siteID, addUserInfo)
      .then(response => {
        //テーブル更新フラグをtrueにする
        props.setAuthInfoChange(true);

        //ダイアログを閉じる
        handleClose();
      })
      .catch(err => {
        if (err.response.data.code === 'LIMIT_EXCEEDED') {
          //サイトの招待人数が101人以上の場合のエラーメッセージ
          alert(trans('UserAddDialog.alertMsg.registerLimit'));
        } else if (err.response.data.code === 'INVALID_PARAMETER') {
          //既に登録済のユーザを招待した場合
          alert(trans('UserAddDialog.alertMsg.invalidAdd'));
        } else if (err.response.data.code === 'FORBIDDEN') {
          //仮パスワードでのログインが完了していないユーザを招待した場合
          alert(trans('UserAddDialog.alertMsg.forbiddenAdd'));
        } else {
          alert(err.message);
        }
      })
      .finally(() => {
        setAPIWait(false);
        //OKボタンを表示する
        setOKBtnDisabled(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIsOpenAddDialog(false);
    setMailAddress('');
    setAuthorization('viewer');
    setMailAddrErrMessage('');
    setAPIWait(false);
  };

  /**
   * 権限選択時の処理
   * @param event
   */
  const handleSelectAuthorization = (
    event: SelectChangeEvent<'admin' | 'manager' | 'viewer'>
  ) => {
    if (
      event.target.value === 'admin' ||
      event.target.value === 'manager' ||
      event.target.value === 'viewer'
    ) {
      setAuthorization(event.target.value);
    }
  };

  return (
    <>
      <AddButton
        onClick={handleClickOpen}
        id="mv-btnAddUser"
        buttonText={trans('UserAddDialog.buttonTxt.addUser')}
      />
      <Dialog
        open={isOpenAddDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        id="mv-form-add-dialog-security">
        <DialogTitle id="form-dialog-title">
          {trans('UserAddDialog.dialogBox.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box className="siteSecurity__message">
              {trans('UserAddDialog.dialogBox.msg')}
            </Box>
            {/* 入力チェック エラーメッセージ */}
            {mailAddrErrMessage === 'FormatFailure' ? (
              <Box className="siteSecurity__text">
                <Box className="siteSecurity__text--error">
                  {trans('UserAddDialog.errorMsg.invalidEmail')}
                </Box>
              </Box>
            ) : (
              <></>
            )}
          </DialogContentText>
          <Stack spacing={2}>
            <ChkInputTextbox
              setValue={setMailAddress}
              setError={setMailAddressError}
              chkInputTxtError={format.BASIC_PATTERN_ERROR}
              chkLength={format.EMAIL_LENGTH_MAX}
              type="text"
              label={trans('UserAddDialog.dialogBox.txtField.label.email')}
              value={mailAddress}
              id="mv-addUserSecurity"
              class="siteSecurity__inputForm--email"
              helperText=" "
              errHelperText={trans(
                'UserAddDialog.dialogBox.txtField.errHelperText'
              )}
              variant="standard"
              autoFocus={true}
            />
            <FormControl
              variant="standard"
              className="siteSecurity__inputForm--selectAuthorization">
              <InputLabel id="demo-simple-select-label">
                {trans(
                  'UserAddDialog.dialogBox.inputForm.label.selectAuthorization'
                )}
              </InputLabel>
              <Select
                variant="standard"
                labelId="demo-simple-select-label"
                id="mv-selectAuthority"
                value={authorization}
                label={trans(
                  'UserAddDialog.dialogBox.inputForm.label.authorization'
                )}
                onChange={handleSelectAuthorization}>
                <MenuItem id="mv-btnAdmin" value="admin">
                  {trans('UserAddDialog.dialogBox.inputForm.item.admin')}
                </MenuItem>
                <MenuItem id="mv-btnManager" value="manager">
                  {trans('UserAddDialog.dialogBox.inputForm.item.manager')}
                </MenuItem>
                <MenuItem id="mv-btnViewer" value="viewer">
                  {trans('UserAddDialog.dialogBox.inputForm.item.viewer')}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('UserAddDialog.dialogBox.buttonTxt.cancel')}
            id="mv-btnCancelAddSecurity"
          />
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('UserAddDialog.dialogBox.buttonTxt.ok')}
            id="mv-btnOkAddSecurity"
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id="mv-userAddErrDlgBtn"
      />
    </>
  );
};
export default UserAddDialog;
